import type { LocationMatchTarget } from 'utils/locationMatch';
import { isMCFCountry } from 'lib/region/countryUtils';

export const POPUP_DISMISSED_KEY = 'UH_sizing-kit-popup-dismissed';
export const SIZING_KIT_CLAIMED_KEY = 'UH_sizing-kit-claimed';
export const DEFAULT_DELAY_MS = 60_000;
export const DEFAULT_TYPEFORM_ID = 'Qry3Lchn';

export type ClaimSizingKitProduct = 'ring_air' | 'ring_pro';
export const DEFAULT_PRODUCT: ClaimSizingKitProduct = 'ring_air';

export const RING_PRO_TYPEFORM_ID = 'Kdk0BeqN';

// Single source of truth for the sizing-kit popup footprint, consumed by
// every mount site (ring/buy IN + AE variants, MCFRing, NonMCFRing,
// ring-pro/buy). Ring AIR: IN/AE/AU + MCF countries minus the exclusions.
// Ring PRO: the same footprint plus US/CA. ROW never matches by construction.
const SIZING_KIT_POPUP_BASE_REGIONS = ['IN', 'AE', 'AU'];
const SIZING_KIT_POPUP_EXCLUDED_REGIONS = ['ES', 'FR'];
const RING_PRO_ONLY_SIZING_KIT_REGIONS = ['US', 'CA'];

export const shouldShowSizingKitPopup = (
  region: string | undefined,
  product: ClaimSizingKitProduct,
): boolean => {
  const r = region?.toUpperCase() ?? '';
  if (SIZING_KIT_POPUP_EXCLUDED_REGIONS.includes(r)) return false;
  if (SIZING_KIT_POPUP_BASE_REGIONS.includes(r) || isMCFCountry(r)) return true;
  return product === 'ring_pro' && RING_PRO_ONLY_SIZING_KIT_REGIONS.includes(r);
};

export const RING_AIR_POPUP_IMAGE = 'web_v2/ring-buy/sizing_kit_free_claim.png';

export const RING_PRO_POPUP_IMAGE =
  'web_v2/ring-pro/sizing-kit-popup/pro-sizing-kit-1.png';

export type ClaimPopupVariant = 'sizing_kit' | 'bangalore_uber';

export const storageKeysForProduct = (product: ClaimSizingKitProduct) => {
  if (product === 'ring_air') {
    return {
      dismissed: POPUP_DISMISSED_KEY,
      claimed: SIZING_KIT_CLAIMED_KEY,
    };
  }
  return {
    dismissed: `${POPUP_DISMISSED_KEY}:${product}`,
    claimed: `${SIZING_KIT_CLAIMED_KEY}:${product}`,
  };
};

export const BANGALORE_EXPERIENCE_CENTRE = {
  latitude: 12.9635736,
  longitude: 77.6414554,
  imageSrc: 'web_v2/ring-buy/PL-15.jpg',
  mapsUrl: 'https://maps.app.goo.gl/KD7Z34GSK4JGpKvx5',
} as const;

export const BANGALORE_CITY_ALIASES = [
  'BANGALORE',
  'BENGALURU',
  'BANGALORE URBAN',
  'BENGALURU URBAN',
] as const;

export const BANGALORE_LOCATION_TARGET: LocationMatchTarget = {
  cityAliases: BANGALORE_CITY_ALIASES,
  latitude: 12.9716,
  longitude: 77.5946,
  radiusKm: 60,
  browserGeoCountries: ['IN'],
};

export const buildUberDeeplinkUrl = (
  latitude: number | string,
  longitude: number | string,
): string =>
  `https://m.uber.com/ul/?action=setPickup&dropoff[latitude]=${latitude}&dropoff[longitude]=${longitude}`;

export const BANGALORE_UBER_DEEPLINK = buildUberDeeplinkUrl(
  BANGALORE_EXPERIENCE_CENTRE.latitude,
  BANGALORE_EXPERIENCE_CENTRE.longitude,
);

// Vercel Analytics (snake_case)
export const ANALYTICS_EVENTS = {
  TIMER_STARTED: 'sizing_kit_popup_timer_started',
  SUPPRESSED: 'sizing_kit_popup_suppressed',
  POPUP_SHOWN: 'sizing_kit_popup_shown',
  POPUP_DISMISSED: 'sizing_kit_popup_dismissed',
  CLAIMED: 'sizing_kit_popup_claimed',
  CANCELLED: 'sizing_kit_popup_cancelled',
};

// GTM dataLayer (camelCase)
export const GTM_EVENTS = {
  TIMER_STARTED: 'sizingKitPopupTimerStarted',
  SUPPRESSED: 'sizingKitPopupSuppressed',
  POPUP_SHOWN: 'sizingKitPopupShown',
  POPUP_DISMISSED: 'sizingKitPopupDismissed',
  CLAIMED: 'sizingKitPopupClaimed',
  CANCELLED: 'sizingKitPopupCancelled',
};
